import { ctxTachograph } from '@/contexts/TachographCtx';
import { TabValue } from '@/types';
import { datePeriodMD } from '@/utilities/dateUtils';
import { ArrowBack, ArrowForward, CalendarMonth, Category, Check } from '@mui/icons-material';
import { Box, IconButton, Tab, Tabs } from '@mui/material';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TachoTabs } from './TachographPopup';

export function TachographMobileHeader({ activeTab }: { activeTab: TachoTabs }) {
  const tachographCtx = useContext(ctxTachograph);
  const { t, i18n } = useTranslation();

  if (!tachographCtx) return;

  const hasDates = Boolean(tachographCtx.selectedDates?.from && tachographCtx.selectedDates?.to);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const intervalLength = useMemo(() => {
    if (hasDates) {
      return datePeriodMD(tachographCtx.selectedDates!.from, tachographCtx.selectedDates!.to, t);
    }
    return '';
  }, [tachographCtx?.selectedDates?.from, tachographCtx?.selectedDates?.to, i18n, hasDates]);

  const onResultTab = tachographCtx.tab === TabValue.result;
  const canGoForward = hasDates && !onResultTab && activeTab !== 'undefined';

  function navigateBack() {
    tachographCtx?.setTab(tachographCtx.tab - 1);
  }

  function navigateForward() {
    tachographCtx?.setTab(TabValue.result);
  }

  function getResultTitle() {
    if (activeTab === 'TargetTab') {
      return t('tachograph.category.vehicleDownload');
    }

    return t('tachograph.category.driverDownload');
  }

  return (
    <>
      <div className="refuel-summary-detail-header">
        {onResultTab && (
          <span className="left">
            <IconButton data-qa-element-id="back_button" onClick={navigateBack} sx={{ color: 'text.primary' }}>
              <ArrowBack />
            </IconButton>
          </span>
        )}
        <div className="refuel-tab-name">
          {onResultTab ? (
            <div className="refuel-tab-name-target">
              <div data-qa-element-id="refuel_result_target_name">{getResultTitle()}</div>
            </div>
          ) : (
            <div data-qa-element-id="refuel_tab_title">{t('tachograph.title')}</div>
          )}
        </div>
        {canGoForward && (
          <span className="right">
            <IconButton data-qa-element-id="forward_button" onClick={navigateForward} sx={{ color: 'text.primary' }}>
              <ArrowForward />
            </IconButton>
          </span>
        )}
      </div>
      {!onResultTab && (
        <Box className="refuel-header">
          <Tabs
            variant="fullWidth"
            value={tachographCtx.tab}
            indicatorColor="secondary"
            textColor="secondary"
            onChange={(_evt, value) => tachographCtx.setTab(value as TabValue)}
          >
            <Tab
              data-qa-element-id="date_selector_tab"
              value={TabValue.targetSelect}
              icon={
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarMonth />
                  {tachographCtx.selectedDates && (
                    <Check color="success" fontSize="medium" sx={{ position: 'absolute', right: -25 }} />
                  )}
                </Box>
              }
              label={
                <div
                  data-qa-element-id={`date_selector_tab_label_${tachographCtx.selectedDates?.from.toLocaleDateString()}_${tachographCtx.selectedDates?.to.toLocaleDateString()}`}
                >
                  {intervalLength || t('refuels.period')}
                </div>
              }
              sx={{ textTransform: 'none' }}
            />

            <Tab
              data-qa-element-id="category_selector_tab"
              value={TabValue.dateSelect}
              icon={<Category />}
              label={t('tachograph.category.mobileTitle')}
              sx={{ textTransform: 'none', opacity: tachographCtx.selectedDates ? 1 : 0 }}
              disabled={tachographCtx.selectedDates ? false : true}
            />
          </Tabs>
        </Box>
      )}
    </>
  );
}
